import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import CustomerSelect from "./CustomerSelect";

const money = (value) => Number(value ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyForm = { customerId: "", salesInvoiceId: "", receiptDate: "", amount: "", mode: "CASH", accountName: "", collectedById: "", remarks: "" };

const PaymentReceipts = () => {
  const [receipts, setReceipts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [salesPeople, setSalesPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadReceipts = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/payment-receipts");
      setReceipts(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load payment receipts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReceipts();
    api.getAll("/api/v1/customers").then((data) => setCustomers(data?.content || [])).catch(() => {});
    api.get("/api/v1/sales-persons").then((data) => setSalesPeople(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setFormData(emptyForm);
    setInvoices([]);
    setShowModal(true);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "customerId") {
      setFormData((prev) => ({ ...prev, salesInvoiceId: "" }));
      if (!value) {
        setInvoices([]);
        return;
      }
      try {
        const data = await api.get("/api/v1/sales-invoices", { size: 200 });
        setInvoices((data?.content || []).filter((inv) => String(inv.customerId) === String(value)));
      } catch (error) {
        setInvoices([]);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/v1/payment-receipts", {
        customerId: Number(formData.customerId),
        salesInvoiceId: formData.salesInvoiceId ? Number(formData.salesInvoiceId) : null,
        receiptDate: formData.receiptDate || undefined,
        amount: Number(formData.amount),
        mode: formData.mode,
        accountName: formData.accountName || undefined,
        collectedById: formData.collectedById ? Number(formData.collectedById) : null,
        remarks: formData.remarks || undefined,
      });
      toast.success("Payment receipt recorded");
      setShowModal(false);
      loadReceipts();
    } catch (error) {
      toast.error(error.message || "Failed to record payment receipt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        Payments received from customers — cash, bank, UPI, or cheque. Link a receipt to a specific invoice to reduce
        its outstanding balance, or leave it unlinked to record it "on account".
      </div>

      <div className="filters-bar">
        <button className="primary" onClick={openCreate} style={{ marginLeft: "auto" }}>
          + New Receipt
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Receipt #</th>
              <th>Date</th>
              <th>Customer</th>
              <th>Invoice #</th>
              <th>Mode</th>
              <th>Account</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {receipts.length === 0 ? (
              <tr>
                <td colSpan={7}>No payment receipts found.</td>
              </tr>
            ) : (
              receipts.map((r) => (
                <tr key={r.id}>
                  <td>{r.receiptNumber}</td>
                  <td>{r.receiptDate}</td>
                  <td>{r.customerName}</td>
                  <td>{r.salesInvoiceNumber || "On account"}</td>
                  <td>{r.mode}</td>
                  <td>{r.accountName || "-"}</td>
                  <td>{money(r.amount)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Record Payment Receipt</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label>Customer (filter by salesman / beats, search by code or name)</label>
                  <CustomerSelect
                    customers={customers}
                    value={formData.customerId}
                    onChange={(value) => handleChange({ target: { name: "customerId", value } })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Against Invoice (optional)</label>
                  <select name="salesInvoiceId" value={formData.salesInvoiceId} onChange={handleChange}>
                    <option value="">On account (no specific invoice)</option>
                    {invoices.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.invoiceNumber}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <input type="number" min="0" step="0.01" name="amount" value={formData.amount} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Mode</label>
                  <select name="mode" value={formData.mode} onChange={handleChange}>
                    <option value="CASH">Cash</option>
                    <option value="BANK">Bank</option>
                    <option value="UPI">UPI</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Account Name (e.g. HDFC Bank)</label>
                  <input name="accountName" value={formData.accountName} onChange={handleChange} placeholder="Cash in Hand" />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" onClick={(e) => e.target.showPicker?.()} name="receiptDate" value={formData.receiptDate} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Collected By (optional)</label>
                  <select name="collectedById" value={formData.collectedById} onChange={handleChange}>
                    <option value="">Unassigned</option>
                    {salesPeople.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.employeeCode}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <input name="remarks" value={formData.remarks} onChange={handleChange} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PaymentReceipts;
